import { useState, useEffect, useCallback } from "react";
import { QrCode } from "lucide-react";
import { parseQRCode } from "../../utils/qrParser";

// Keys to ignore when scanning (defined outside component to avoid re-creation)
const EXTRANEOUS_KEYS = ["Shift", "Control", "Alt", "Meta", "CapsLock", "Tab"];

const ScanBadgeButton = ({
  scannedReturnedID,
  setScannedReturnedID,
  disabled,
}) => {
  const [isScanning, setIsScanning] = useState(false);
  const [scannedCode, setScannedCode] = useState([]);
  const [scanTimeout, setScanTimeout] = useState(null);

  // Reset scanning after 30 seconds of inactivity
  const resetScanning = useCallback(() => {
    setIsScanning(false);
    setScannedCode([]);
  }, []);

  // Stop scanning if button becomes disabled
  useEffect(() => {
    if (disabled && isScanning) {
      resetScanning();
    }
  }, [disabled, isScanning, resetScanning]);

  // Handle keyboard input when scanning is active
  useEffect(() => {
    if (!isScanning || disabled) return;

    const handleKeyDown = (e) => {
      // Reset the timeout on each keypress
      if (scanTimeout) {
        clearTimeout(scanTimeout);
      }

      // Set new timeout for 30 seconds
      const timeout = setTimeout(resetScanning, 30000);
      setScanTimeout(timeout);

      if (e.key === "Enter") {
        const rawCode = scannedCode
          .filter((x) => !EXTRANEOUS_KEYS.includes(x))
          .join("");

        if (rawCode) {
          // parse qr
          const userId = parseQRCode(rawCode);

          if (userId) {
            setScannedReturnedID(userId);
          } else {
            console.warn(
              "QR code did not match expected format (https://trunk.treehacks.com/p/<id>):",
              rawCode,
            );
            setScannedReturnedID(rawCode);
          }
          resetScanning();
        }

        setScannedCode([]);
      } else {
        setScannedCode((prevScannedCode) => [...prevScannedCode, e.key]);
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    // Cleanup function
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      if (scanTimeout) {
        clearTimeout(scanTimeout);
      }
    };
  }, [
    isScanning,
    scannedCode,
    scanTimeout,
    setScannedReturnedID,
    resetScanning,
    disabled,
  ]);

  // Handle window focus/blur
  useEffect(() => {
    const onBlur = () => {
      resetScanning();
    };

    window.addEventListener("blur", onBlur);
    return () => {
      window.removeEventListener("blur", onBlur);
    };
  }, [resetScanning]);

  // Handle paste anywhere on the page as a scan
  useEffect(() => {
    if (disabled) return;

    const handlePaste = (e) => {
      // Ignore pastes into input/textarea elements
      const tag = e.target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;

      const text = (e.clipboardData || window.clipboardData)
        ?.getData("text")
        ?.trim();
      if (!text) return;

      const userId = parseQRCode(text);
      if (userId) {
        setScannedReturnedID(userId);
      } else if (/^[a-fA-F0-9]{24}$/.test(text)) {
        setScannedReturnedID(text);
      }
    };

    document.addEventListener("paste", handlePaste);
    return () => document.removeEventListener("paste", handlePaste);
  }, [disabled, setScannedReturnedID]);

  return (
    <button
      onClick={() => !disabled && setIsScanning(!isScanning)}
      disabled={disabled}
      className={`inline-flex items-center px-4 py-2 rounded-lg space-x-2 text-white transition-colors ${
        disabled
          ? "bg-gray-300 cursor-not-allowed"
          : isScanning
            ? "bg-red-500 hover:bg-red-600"
            : "bg-tree-green hover:bg-tree-green/90"
      }`}
    >
      <QrCode className={`h-5 w-5 ${isScanning ? "animate-pulse" : ""}`} />
      <span>{isScanning ? "Scanning..." : "Scan Badge"}</span>
    </button>
  );
};

export default ScanBadgeButton;
